import {getLocale} from './localization'
import type {SupportedLocale} from './localization'
import type {Country} from '../../components/forms/Form/components/ConsentExperience/types'
import countriesDe from '../../components/forms/Form/components/ConsentExperience/CountrySelect/countries/de'
import countriesEs from '../../components/forms/Form/components/ConsentExperience/CountrySelect/countries/es'
import countriesFr from '../../components/forms/Form/components/ConsentExperience/CountrySelect/countries/fr'
import countriesJa from '../../components/forms/Form/components/ConsentExperience/CountrySelect/countries/ja'
import countriesKo from '../../components/forms/Form/components/ConsentExperience/CountrySelect/countries/ko'
import countriesPt from '../../components/forms/Form/components/ConsentExperience/CountrySelect/countries/pt'

// es/ja/ko/pt are full curated lists; de/fr are partial overlays — anything not in the overlay falls back to CLDR.
const CURATED_COUNTRY_LISTS: Partial<Record<SupportedLocale, readonly Country[]>> = {
  de: countriesDe,
  es: countriesEs,
  fr: countriesFr,
  ja: countriesJa,
  ko: countriesKo,
  pt: countriesPt,
}

const _curatedLookupCache = new Map<SupportedLocale, Map<string, string>>()
function getCuratedLookup(locale: SupportedLocale): Map<string, string> | null {
  const list = CURATED_COUNTRY_LISTS[locale]
  if (!list) return null
  let cached = _curatedLookupCache.get(locale)
  if (!cached) {
    cached = new Map(list.map(c => [c.alpha, c.name]))
    _curatedLookupCache.set(locale, cached)
  }
  return cached
}

// Keyed per locale so processes serving multiple locales (and tests that swap `getLocale()`) don't reuse a stale instance.
const _regionDisplayNamesCache = new Map<SupportedLocale, Intl.DisplayNames>()
function getRegionDisplayNames(): Intl.DisplayNames {
  const locale = getLocale()
  let instance = _regionDisplayNamesCache.get(locale)
  if (!instance) {
    instance = new Intl.DisplayNames([locale], {type: 'region'})
    _regionDisplayNamesCache.set(locale, instance)
  }
  return instance
}

const _nameCollatorCache = new Map<SupportedLocale, Intl.Collator>()

function getCountryNameCollator(): Intl.Collator {
  const locale = getLocale()
  let instance = _nameCollatorCache.get(locale)
  if (!instance) {
    instance = new Intl.Collator(locale, {sensitivity: 'base'})
    _nameCollatorCache.set(locale, instance)
  }
  return instance
}

export function getCountryName(alpha: string): string {
  const curated = getCuratedLookup(getLocale())?.get(alpha)
  if (curated) return curated
  return getRegionDisplayNames().of(alpha) ?? alpha
}

export function sortByLocalizedName<T>(items: readonly T[], getName: (item: T) => string): T[] {
  const collator = getCountryNameCollator()
  return [...items].sort((a, b) => collator.compare(getName(a), getName(b)))
}
